import React, {
  BaseSyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  EventType,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useFormContext,
  WatchObserver,
} from 'react-hook-form';

import { getDirtyFields } from 'utils/calculateDirtyFields';
import { ObjectPaths } from 'utils/objectPaths';

export interface Subscription {
  unsubscribe(): void;
}

type FormEventType =
  | 'focus'
  | 'blur'
  | 'change'
  | 'changeText'
  | 'valueChange'
  | 'endEditing'
  | 'keyPress'
  | 'selectionChange'
  | 'longPress'
  | 'press'
  | 'pressIn'
  | 'pressOut'
  | 'load'
  | 'error'
  | 'progress'
  | 'custom';

const formEventTypes: Record<string, boolean> = {
  focus: true,
  blur: true,
  change: true,
  changeText: true,
  valueChange: true,
  endEditing: true,
  keyPress: true,
  selectionChange: true,
  longPress: true,
  press: true,
  pressIn: true,
  pressOut: true,
  load: true,
  error: true,
  progress: true,
  custom: true,
};

function isFormEventType(value: string): value is FormEventType {
  return formEventTypes[value] !== undefined;
}

export type FormEventObserver = (
  type: FormEventType,
  name: string | undefined
) => void;

export interface FormEnvironmentRaw<FieldsTypes extends FieldValues> {
  getDefaultValue<T = string>(path: string): T | undefined;
  getDefaultValues(): object | undefined;
  getValue<T = string>(path?: string): T | undefined;
  setValue(
    path: string,
    value: unknown,
    opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
  ): void;
  setError(path: string, error: { type: string; message: string }): void;
  resetField(path: string, defaultValue: any): void;
  reset(values?: FieldsTypes, options?: Record<string, boolean>): void;
  getFormState(): {
    isDirty: boolean;
    dirtyFields: object;
    isSubmitting: boolean;
    isValid: boolean;
    errors: object;
    isSubmitSuccessful: boolean;
  };
  getPatchObject(): any;
  getPatchObjectWithFormData(data: any): any;
  watch: (callback: WatchObserver<Record<string, any>>) => Subscription;
  triggerFormValidation: (paths?: string[]) => Promise<boolean>;
  clearErrors(path?: string[] | string): void;
  watchValue<T = unknown>(value: string, defaultValue?: T): T | undefined;
  setFocus(path: string): void;
  submitFactory(
    onValid: (
      data: FieldsTypes,
      event?: BaseSyntheticEvent
      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    ) => unknown | Promise<unknown>
  ): (e?: BaseSyntheticEvent) => Promise<void>;
}

interface FormEnvironment<TFormModel = Record<string, any>> {
  getDefaultValue<T = string>(path: ObjectPaths<TFormModel>): T | undefined;
  /* Won't re-render if the value is changed. Use watchValue for re-renders */
  getValue<T = string>(path?: ObjectPaths<TFormModel>): T | undefined;
  getAllValues(): TFormModel | undefined;
  setValue(
    path: ObjectPaths<TFormModel>,
    value: unknown,
    opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
  ): void;
  setError(
    path: ObjectPaths<TFormModel>,
    error: { type: string; message: string }
  ): void;
  resetField(path: ObjectPaths<TFormModel>, defaultValue: any): void;
  reset(values?: TFormModel, options?: Record<string, boolean>): void;
  /** @deprecated - use {@link useFormStateInternal} */
  getFormState(): {
    isDirty: boolean;
    dirtyFields: object;
    isSubmitting: boolean;
    isValid: boolean;
    errors: Record<string, any>;
    isSubmitSuccessful: boolean;
  };

  /**
   * @deprecated use {@link getPatchObjectWithFormData}
   * This has an inherent design flaw where it uses all values of the form rather than non-disabled
   * fields.
   */
  getPatchObject(): Partial<TFormModel> | undefined;
  getPatchObjectWithFormData(data: any): Partial<TFormModel> | undefined;
  subscribe: (observer: FormEventObserver, events?: FormEventType[]) => void;
  triggerFormValidation: (
    paths?: ObjectPaths<TFormModel>[]
  ) => Promise<boolean>;
  clearErrors(path?: ObjectPaths<TFormModel> | ObjectPaths<TFormModel>[]): void;
  /**
   * @deprecated use {@link useFormWatch}
   * Only use if the path is registered with RHF, otherwise a change in value won't cause
   * a rerender.
   */
  watchValue<T = unknown>(value: string, defaultValue?: T): T | undefined;
  setFocus(path: ObjectPaths<TFormModel>): void;
  submitFactory(
    onValid: (data: TFormModel, event?: BaseSyntheticEvent) => unknown
  ): (e?: BaseSyntheticEvent) => Promise<void>;
}

export type FormEnvironmentBase<TFormModel> = Omit<
  FormEnvironment<TFormModel>,
  'subscribe' | 'watchValue'
>;

interface SubFormEnvironment<TModel, TFormModel = Record<string, any>> {
  getDefaultValue<T = string>(path: ObjectPaths<TModel>): T | undefined;
  getValue<T = string>(path: ObjectPaths<TModel>): T | undefined;
  setValue(
    path: ObjectPaths<TModel>,
    value: unknown,
    opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
  ): void;
  setError(
    path: ObjectPaths<TModel>,
    error: { type: string; message: string }
  ): void;
  resetField(path: ObjectPaths<TModel>, defaultValue: any): void;

  getFormDefaultValue<T = string>(path: ObjectPaths<TFormModel>): T | undefined;
  getFormValue<T = string>(path: ObjectPaths<TFormModel>): T | undefined;
  setFormValue(path: ObjectPaths<TFormModel>, value: unknown): void;
  setFormError(
    path: ObjectPaths<TFormModel>,
    error: { type: string; message: string }
  ): void;
  resetFormField(path: ObjectPaths<TFormModel>, defaultValue: any): void;
  setFocus(path: ObjectPaths<TModel>): void;
}

export function buildFieldPathInForm<TFormModel, TModel>(
  relativePath: ObjectPaths<TModel>,
  formPath?: ObjectPaths<TFormModel>
): ObjectPaths<TFormModel> {
  const result: string = !formPath
    ? relativePath
    : !relativePath
      ? formPath
      : `${formPath}.${relativePath}`;
  return result as ObjectPaths<TFormModel>;
}

export function buildBaseFormEnvironment<
  TFormModel extends FieldValues = Record<string, any>,
>(data: FormEnvironmentRaw<TFormModel>): FormEnvironmentBase<TFormModel> {
  return {
    submitFactory(
      onValid: (data: TFormModel, event?: React.BaseSyntheticEvent) => unknown
    ): (e?: React.BaseSyntheticEvent) => Promise<void> {
      return data.submitFactory(onValid);
    },
    setFocus(path: ObjectPaths<TFormModel>) {
      return data.setFocus(path);
    },
    triggerFormValidation(paths?: ObjectPaths<TFormModel>[]): Promise<boolean> {
      return data.triggerFormValidation(paths);
    },
    getValue<T = string>(path?: ObjectPaths<TFormModel>): T | undefined {
      return data.getValue<T>(path);
    },
    getAllValues(): TFormModel | undefined {
      return data.getValue<TFormModel>();
    },
    setValue(
      path: ObjectPaths<TFormModel>,
      value: unknown,
      opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
    ): void {
      data.setValue(path, value, opts);
    },
    resetField(path: ObjectPaths<TFormModel>, defaultValue: any): void {
      data.resetField(path, defaultValue);
    },
    reset(values?: TFormModel, options?: Record<string, boolean>): void {
      data.reset(values, options);
    },
    getDefaultValue<T = string>(path: ObjectPaths<TFormModel>): T | undefined {
      return data.getDefaultValue<T>(path);
    },
    getFormState(): {
      isDirty: boolean;
      dirtyFields: object;
      isSubmitting: boolean;
      isValid: boolean;
      errors: object;
      isSubmitSuccessful: boolean;
    } {
      const { dirtyFields, isSubmitting, isValid, errors, isSubmitSuccessful } =
        data.getFormState();

      let currentDirtyFields: object = dirtyFields;
      if (!currentDirtyFields || Object.keys(currentDirtyFields).length === 0) {
        currentDirtyFields = getDirtyFields(
          data.getValue<TFormModel>(),
          data.getDefaultValues()
        );
      }

      return {
        isSubmitSuccessful,
        dirtyFields,
        isSubmitting,
        isValid,
        errors,
        isDirty:
          currentDirtyFields && Object.keys(currentDirtyFields).length > 0,
      };
    },
    setError(
      path: ObjectPaths<TFormModel>,
      error: { type: string; message: string }
    ): void {
      return data.setError(path, error);
    },
    getPatchObject(): Partial<TFormModel> | undefined {
      return data.getPatchObject();
    },
    getPatchObjectWithFormData(formData: any): Partial<TFormModel> | undefined {
      return data.getPatchObjectWithFormData(formData);
    },
    clearErrors(
      path?: ObjectPaths<TFormModel> | ObjectPaths<TFormModel>[]
    ): void {
      return data.clearErrors(path);
    },
  };
}

function buildFormEnvironment<
  TFormModel extends FieldValues = Record<string, any>,
>(
  data: FormEnvironmentRaw<TFormModel>,
  env: {
    subscribe: (observer: FormEventObserver, events?: FormEventType[]) => void;
  }
): Omit<FormEnvironment<TFormModel>, 'watchValue'> {
  const base = buildBaseFormEnvironment<TFormModel>(data);
  return {
    ...base,
    subscribe: env.subscribe,
  };
}

function buildSubFormEnvironment<
  TModel,
  TFormModel extends FieldValues = Record<string, any>,
>(
  data: FormEnvironmentRaw<TFormModel>,
  formPath?: ObjectPaths<TFormModel>
): SubFormEnvironment<TModel, TFormModel> {
  const buildFormPath = (path: ObjectPaths<TModel>) =>
    buildFieldPathInForm(path, formPath);

  return {
    setFocus(path: ObjectPaths<TModel>) {
      return data.setFocus(buildFormPath(path));
    },
    getValue<T = string>(path: ObjectPaths<TModel>): T | undefined {
      return data.getValue<T>(buildFormPath(path));
    },
    setValue(
      path: ObjectPaths<TModel>,
      value: unknown,
      opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
    ) {
      data.setValue(buildFormPath(path), value, opts);
    },
    getDefaultValue<T = string>(path: ObjectPaths<TModel>): T | undefined {
      return data.getDefaultValue<T>(buildFormPath(path));
    },
    resetField(path: ObjectPaths<TModel>, defaultValue: any) {
      data.setValue(buildFormPath(path), defaultValue);
    },
    setError(
      path: ObjectPaths<TModel>,
      error: { type: string; message: string }
    ) {
      data.setError(buildFormPath(path), error);
    },
    getFormValue<T = string>(path: ObjectPaths<TFormModel>): T | undefined {
      return data.getValue<T>(path);
    },
    getFormDefaultValue<T = string>(
      path: ObjectPaths<TFormModel>
    ): T | undefined {
      return data.getDefaultValue<T>(path);
    },
    setFormValue(path: ObjectPaths<TFormModel>, value: unknown) {
      data.setValue(path, value);
    },
    resetFormField(path: ObjectPaths<TFormModel>, defaultValue: any) {
      data.resetField(path, defaultValue);
    },
    setFormError(
      path: ObjectPaths<TFormModel>,
      error: { type: string; message: string }
    ) {
      data.setError(path, error);
    },
  };
}

export const FormEnvironmentContext = createContext<
  FormEnvironmentRaw<Record<string, any>>
>({} as FormEnvironmentRaw<Record<string, any>>);

const timerTimeoutMsec = 200;
const timerActiveTimeSec = 60;
const timerMaxTicks = (timerActiveTimeSec * 1000) / timerTimeoutMsec;

function useFormObservers<TFormModel extends FieldValues>(
  data: FormEnvironmentRaw<TFormModel>
) {
  const watcherSubscription = useRef<Subscription | undefined>(undefined);
  const formEventObservers = useRef<
    {
      observer: FormEventObserver;
      events?: FormEventType[];
    }[]
  >([]);
  const eventToProcess = useRef<
    | {
        observers: FormEventObserver[];
        eventType: FormEventType;
        name: string | undefined;
      }
    | undefined
  >(undefined);
  const timerId = useRef<number | undefined>(undefined);
  const timerIdleCounter = useRef<number>(0);

  const stopTimer = useCallback(() => {
    if (timerId.current !== undefined) {
      timerIdleCounter.current = 0;
      window.clearInterval(timerId.current);
      timerId.current = undefined;
    }
  }, []);

  const processEvent = useCallback(
    (
      eventType: FormEventType,
      name: string | undefined,
      observers: FormEventObserver[]
    ) => {
      observers.forEach((observer) => {
        observer(eventType, name);
      });
    },
    []
  );

  const timerProcessor = useCallback(() => {
    if (eventToProcess.current === undefined) {
      if (timerIdleCounter.current < timerMaxTicks) {
        timerIdleCounter.current = timerIdleCounter.current + 1;
      } else {
        stopTimer();
      }
      return;
    }
    timerIdleCounter.current = 0;
    const { observers, eventType, name } = eventToProcess.current;
    eventToProcess.current = undefined;
    processEvent(eventType, name, observers);
  }, [processEvent, stopTimer]);

  const startWatcher = useCallback(
    (): Subscription =>
      data.watch((_, info): void => {
        const infoType: EventType = info.type ?? 'custom';
        if (!isFormEventType(infoType)) return;
        const observers = formEventObservers.current
          .filter(({ events }) =>
            events === undefined || events.length === 0
              ? true
              : events.find((e) => e === infoType) !== undefined
          )
          .map((pair) => pair.observer);
        if (observers.length === 0) return;
        if (timerId.current === undefined) {
          eventToProcess.current = undefined;
          timerId.current = window.setInterval(
            timerProcessor,
            timerTimeoutMsec
          );
          processEvent(infoType, info.name, observers);
        } else {
          eventToProcess.current = {
            observers,
            eventType: infoType,
            name: info.name,
          };
        }
      }),
    [data, processEvent, timerProcessor]
  );

  const stopWatcher = useCallback(() => {
    watcherSubscription.current?.unsubscribe();
    stopTimer();
  }, [stopTimer]);

  useEffect(() => () => stopWatcher(), [stopWatcher]);

  const subscribe = useCallback(
    (observer: FormEventObserver, events?: FormEventType[]): void => {
      formEventObservers.current = [
        ...formEventObservers.current,
        { observer, events },
      ];
      if (watcherSubscription.current === undefined) {
        watcherSubscription.current = startWatcher();
      }
    },
    [startWatcher]
  );

  return useMemo(() => ({ subscribe }), [subscribe]);
}

export function useFormEnvironment<
  TFormModel extends FieldValues = Record<string, any>,
>(): FormEnvironment<TFormModel> {
  const data = useContext(
    FormEnvironmentContext
  ) as FormEnvironmentRaw<TFormModel>;
  const { watch, setValue: setValueBare } = useFormContext();

  const watchValue = useCallback(
    <T = unknown>(value: string, defaultValue?: T): T | undefined =>
      watch(value, defaultValue),
    [watch]
  );

  const setValue = useCallback(
    (
      path: ObjectPaths<TFormModel>,
      value: unknown,
      opts?: Partial<{ shouldValidate: boolean; shouldDirty: boolean }>
    ): void => {
      setValueBare(
        path,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        value as FieldPathValue<FieldValues, FieldPath<FieldValues>>,
        opts
      );
    },
    [setValueBare]
  );

  const { subscribe } = useFormObservers<TFormModel>(data);
  return useMemo(
    () => ({
      ...buildFormEnvironment<TFormModel>(data, { subscribe }),
      setValue,
      watchValue,
    }),
    [data, setValue, subscribe, watchValue]
  );
}

export function useSubFormEnvironment<
  TModel,
  TFormModel extends FieldValues = Record<string, any>,
>(formPath?: ObjectPaths<TFormModel>): SubFormEnvironment<TModel, TFormModel> {
  const data = useContext(
    FormEnvironmentContext
  ) as FormEnvironmentRaw<TFormModel>;
  return useMemo(
    () => buildSubFormEnvironment<TModel, TFormModel>(data, formPath),
    [data, formPath]
  );
}
